import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

const GAME_DATA = {
  rijeci: {
    1: { pocetno_slovo: "g", rest: "aicnra", tocna_rijec: "granica", pozicija_za_frazu: 3 },
    2: { pocetno_slovo: "k", rest: "alurtu", tocna_rijec: "kultura", pozicija_za_frazu: 5 },
  },
  solution: {
    setup: "na cedulji je pisalo",
    fraza: "čuvaj plavi kišobran",
  },
};

function getRows(gameData) {
  return Array.from({ length: 18 }).map((_, i) => {
    const id = String(i + 1);
    const item = gameData.rijeci?.[id] || {};
    return {
      id,
      firstLetter: (item.pocetno_slovo || "").slice(0, 1).toUpperCase(),
      restLetters: (item.rest || "").slice(0, 6).toUpperCase(),
      phraseIndex: Math.max(1, Math.min(7, Number(item.pozicija_za_frazu) || 1)),
      correctWord: (item.tocna_rijec || "").slice(0, 7).toUpperCase(),
    };
  });
}

function decodeBase64Utf8Json(base64Payload) {
  const binary = atob(base64Payload);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  const jsonString = new TextDecoder("utf-8").decode(bytes);
  return JSON.parse(jsonString);
}

function djb2Hash(input) {
  let hash = 5381;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash * 33) ^ input.charCodeAt(i);
  }
  return (hash >>> 0).toString(16);
}

const CROSSWORD_PROGRESS_PREFIX = "socialDeduction.crossword.v1";

function getCrosswordStorageKey(puzzleKey) {
  return `${CROSSWORD_PROGRESS_PREFIX}:${puzzleKey}`;
}

function createEmptyAnswers() {
  return Array.from({ length: 18 }, () => Array(6).fill(""));
}

function createEmptyRevealMask() {
  return Array.from({ length: 18 }, () => Array(6).fill(false));
}

export default function Crossword() {
  const [gameData, setGameData] = useState(GAME_DATA);
  const [lastPayload, setLastPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const rows = useMemo(() => getRows(gameData), [gameData]);
  const puzzleKey = useMemo(() => {
    if (lastPayload) {
      return djb2Hash(lastPayload);
    }
    return `local-${djb2Hash(JSON.stringify(gameData))}`;
  }, [lastPayload, gameData]);

  const inputRefs = useRef([]);
  const checkResultsTimeoutRef = useRef(null);
  const progressSaveTimeoutRef = useRef(null);
  const progressHydratedRef = useRef(false);
  const [answers, setAnswers] = useState(() => createEmptyAnswers());
  const [checkResults, setCheckResults] = useState([]);
  const [showGiveUpModal, setShowGiveUpModal] = useState(false);
  /** Nakon odustajanja: true = ova input ćelija je autofillana jer korisnik nije imao točno slovo */
  const [giveUpRevealedCells, setGiveUpRevealedCells] = useState([]);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  useEffect(() => {
    const loadPuzzle = async () => {
      try {
        setLoading(true);
        setLoadError("");

        const response = await fetch("https://api.profitmap.app/health/daily-puzzle");
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const responseJson = await response.json();
        if (!responseJson?.payload || typeof responseJson.payload !== "string") {
          throw new Error("Payload nije valjan.");
        }

        const decoded = decodeBase64Utf8Json(responseJson.payload);
        setLastPayload(responseJson.payload);
        setGameData(decoded);
      } catch (error) {
        setLoadError("Ne mogu učitati dnevnu zagonetku. Prikazan je zadani primjer.");
      } finally {
        setLoading(false);
      }
    };

    loadPuzzle();
  }, []);

  useLayoutEffect(() => {
    progressHydratedRef.current = false;
    if (progressSaveTimeoutRef.current) {
      clearTimeout(progressSaveTimeoutRef.current);
      progressSaveTimeoutRef.current = null;
    }

    inputRefs.current = [];
    setCheckResults([]);
    setShowGiveUpModal(false);

    const storageKey = getCrosswordStorageKey(puzzleKey);
    let restoredAnswers = null;
    let restoredReveal = null;

    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.puzzleKey === puzzleKey && Array.isArray(parsed.answers)) {
          restoredAnswers = parsed.answers;
        }
        if (parsed?.puzzleKey === puzzleKey && Array.isArray(parsed.giveUpRevealedCells)) {
          restoredReveal = parsed.giveUpRevealedCells;
        }
      }
    } catch {
      restoredAnswers = null;
      restoredReveal = null;
    }

    const nextAnswers = createEmptyAnswers();
    const nextReveal = createEmptyRevealMask();

    if (restoredAnswers) {
      for (let r = 0; r < 18; r += 1) {
        const line = restoredAnswers[r];
        if (!Array.isArray(line)) continue;
        for (let c = 0; c < 6; c += 1) {
          const value = typeof line[c] === "string" ? line[c].toUpperCase() : "";
          nextAnswers[r][c] = value.replace(/[^A-ZČĆŽŠĐ]/g, "").slice(0, 1);
        }
      }
    }

    if (restoredReveal) {
      for (let r = 0; r < 18; r += 1) {
        const line = restoredReveal[r];
        if (!Array.isArray(line)) continue;
        for (let c = 0; c < 6; c += 1) {
          nextReveal[r][c] = Boolean(line[c]);
        }
      }
    }

    setAnswers(nextAnswers);
    setGiveUpRevealedCells(nextReveal);
    progressHydratedRef.current = true;
  }, [puzzleKey]);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!progressHydratedRef.current || loading) {
      return;
    }

    if (progressSaveTimeoutRef.current) {
      clearTimeout(progressSaveTimeoutRef.current);
    }

    progressSaveTimeoutRef.current = setTimeout(() => {
      try {
        const storageKey = getCrosswordStorageKey(puzzleKey);
        const payload = {
          v: 1,
          puzzleKey,
          answers,
          giveUpRevealedCells,
          savedAt: Date.now(),
        };
        localStorage.setItem(storageKey, JSON.stringify(payload));
      } catch {
        // ignore quota / private mode errors
      } finally {
        progressSaveTimeoutRef.current = null;
      }
    }, 350);

    return () => {
      if (progressSaveTimeoutRef.current) {
        clearTimeout(progressSaveTimeoutRef.current);
        progressSaveTimeoutRef.current = null;
      }
    };
  }, [answers, giveUpRevealedCells, puzzleKey, loading]);

  const isMobile = viewportWidth <= 768;
  const cellSize = isMobile ? 20 : 34;
  const circleSize = isMobile ? 30 : 48;
  const arrowCol = isMobile ? 16 : 24;
  const rowStatusWidth = isMobile ? 20 : 28;
  const rowGap = isMobile ? 2 : 6;
  const inputFontSize = isMobile ? "0.85rem" : "1rem";

  const normalizeWord = (word) => (word || "").toLocaleUpperCase("hr-HR");

  const canPlaceLetter = (rowIndex, boxIndex, char) => {
    if (!char) return true;

    const allowedLetters = (rows[rowIndex]?.restLetters || "").split("");
    const allowedCounts = allowedLetters.reduce((acc, letter) => {
      acc[letter] = (acc[letter] || 0) + 1;
      return acc;
    }, {});

    const usedCounts = (answers[rowIndex] || [])
      .filter((_, idx) => idx !== boxIndex)
      .filter(Boolean)
      .reduce((acc, letter) => {
        acc[letter] = (acc[letter] || 0) + 1;
        return acc;
      }, {});

    const nextCount = (usedCounts[char] || 0) + 1;
    return Boolean(allowedCounts[char]) && nextCount <= allowedCounts[char];
  };

  const getUsedCounts = (rowIndex) =>
    (answers[rowIndex] || []).filter(Boolean).reduce((acc, letter) => {
      acc[letter] = (acc[letter] || 0) + 1;
      return acc;
    }, {});

  const isLeftLetterConsumed = (rowIndex, letter, letterIndex) => {
    if (!letter) return true;
    const usedCounts = getUsedCounts(rowIndex);
    const usedCountForLetter = usedCounts[letter] || 0;
    const occurrenceIndex = (rows[rowIndex]?.restLetters || "")
      .slice(0, letterIndex + 1)
      .split("")
      .filter((char) => char === letter).length;
    return occurrenceIndex <= usedCountForLetter;
  };

  const findNextEmptyBoxIndex = (line, startFrom) => {
    for (let i = startFrom + 1; i < 6; i += 1) {
      if (!line[i]) return i;
    }
    for (let i = 0; i <= startFrom; i += 1) {
      if (!line[i]) return i;
    }
    return -1;
  };

  const focusRowBox = (rowIndex, boxIndex) => {
    if (boxIndex < 0 || boxIndex > 5) return;
    window.setTimeout(() => {
      inputRefs.current[rowIndex]?.[boxIndex]?.focus();
    }, 0);
  };

  const handleLetterChange = (rowIndex, boxIndex, value) => {
    const raw = value ?? "";
    const char =
      raw === ""
        ? ""
        : raw.slice(-1).toUpperCase().replace(/[^A-ZČĆŽŠĐ]/g, "");
    if (char && !canPlaceLetter(rowIndex, boxIndex, char)) {
      return;
    }

    setGiveUpRevealedCells((prev) => {
      if (!prev?.length) return prev;
      const next = prev.map((line) => (line ? [...line] : line));
      if (next[rowIndex]?.[boxIndex]) {
        next[rowIndex][boxIndex] = false;
      }
      return next;
    });

    setAnswers((prev) => {
      const next = prev.map((line) => [...line]);
      next[rowIndex][boxIndex] = char;

      if (char) {
        const nextLine = [...(next[rowIndex] || Array(6).fill(""))];
        const nextEmpty = findNextEmptyBoxIndex(nextLine, boxIndex);
        if (nextEmpty !== -1) {
          focusRowBox(rowIndex, nextEmpty);
        }
      }

      return next;
    });
  };

  const handleLeftLetterClick = (rowIndex, letter) => {
    const char = (letter || "").toUpperCase();
    const firstFreeIndex = (answers[rowIndex] || []).findIndex((value) => !value);
    if (firstFreeIndex === -1) return;
    if (!canPlaceLetter(rowIndex, firstFreeIndex, char)) return;

    setAnswers((prev) => {
      const next = prev.map((line) => [...line]);
      next[rowIndex][firstFreeIndex] = char;

      const nextLine = [...(next[rowIndex] || Array(6).fill(""))];
      const nextEmpty = findNextEmptyBoxIndex(nextLine, firstFreeIndex);
      if (nextEmpty !== -1) {
        focusRowBox(rowIndex, nextEmpty);
      } else {
        focusRowBox(rowIndex, firstFreeIndex);
      }

      return next;
    });
  };

  const handleKeyDown = (rowIndex, boxIndex, event) => {
    if (event.key === "Backspace" && !answers[rowIndex]?.[boxIndex] && boxIndex > 0) {
      inputRefs.current[rowIndex]?.[boxIndex - 1]?.focus();
    }
  };

  const handleCheck = () => {
    const results = rows.map((row, rowIndex) => {
      if (!row.correctWord) {
        return null;
      }
      const typedWord = `${row.firstLetter}${(answers[rowIndex] || []).join("")}`;
      return normalizeWord(typedWord) === normalizeWord(row.correctWord);
    });
    setCheckResults(results);

    if (checkResultsTimeoutRef.current) {
      clearTimeout(checkResultsTimeoutRef.current);
    }
    checkResultsTimeoutRef.current = setTimeout(() => {
      setCheckResults([]);
      checkResultsTimeoutRef.current = null;
    }, 3500);
  };

  const handleGiveUpConfirm = () => {
    const prevAnswers = answers.map((line) => [...line]);
    const revealedMask = rows.map((row, rowIndex) => {
      const word = normalizeWord(row.correctWord || "");
      if (word.length !== 7) {
        return Array(6).fill(false);
      }
      const expectedTail = word.slice(1).split("");
      return expectedTail.map((correctLetter, i) => {
        const userLetter = normalizeWord(prevAnswers[rowIndex]?.[i] || "");
        return userLetter !== correctLetter;
      });
    });

    const solvedAnswers = rows.map((row) => {
      const word = normalizeWord(row.correctWord || "");
      if (word.length === 7) {
        return word.slice(1).split("");
      }
      return Array(6).fill("");
    });
    setGiveUpRevealedCells(revealedMask);
    setAnswers(solvedAnswers);
    setShowGiveUpModal(false);
    setCheckResults([]);
  };

  useEffect(() => {
    return () => {
      if (checkResultsTimeoutRef.current) {
        clearTimeout(checkResultsTimeoutRef.current);
      }
    };
  }, []);

  const phraseLetters = useMemo(
    () =>
      rows.map((row, rowIndex) => {
        // Always mirror the currently circled box to the yellow circle,
        // independent from whether the rest of the word is filled.
        if (row.phraseIndex === 1) {
          return row.firstLetter || "";
        }
        return answers[rowIndex]?.[row.phraseIndex - 2] || "";
      }),
    [rows, answers]
  );

  const phraseCircleColors = useMemo(() => {
    const words = (gameData.solution?.fraza || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean);

    const colorsByRow = [];
    words.forEach((word, wordIndex) => {
      const cleanWord = word.replace(/[^\p{L}]/gu, "");
      const color = wordIndex % 2 === 0 ? styles.circleBlue.backgroundColor : styles.circleYellow.backgroundColor;
      for (let i = 0; i < cleanWord.length; i += 1) {
        colorsByRow.push(color);
      }
    });

    return colorsByRow;
  }, [gameData]);

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Riječ na riječ</h1>
      <p style={styles.titleHint}>(nj i lj se pišu kroz dvije kućice)</p>
      <br />
      <p style={styles.subtitle}>{(gameData.solution?.setup || "").toUpperCase()}</p>
      {loading && <p style={styles.infoText}>Učitavanje dnevne zagonetke...</p>}
      {loadError && <p style={styles.errorText}>{loadError}</p>}

      <div style={styles.board}>
        {rows.map((row, rowIndex) => (
          <div
            style={{
              ...styles.row,
              gridTemplateColumns: `${rowStatusWidth}px auto ${arrowCol}px auto ${arrowCol}px ${circleSize}px`,
              gap: `${rowGap}px`,
            }}
            key={row.id}
          >
            <div style={{ ...styles.rowStatus, width: `${rowStatusWidth}px`, fontSize: isMobile ? "0.95rem" : "1.1rem" }}>
              {checkResults[rowIndex] === true && "✅"}
              {checkResults[rowIndex] === false && "❌"}
            </div>
            <div
              style={{
                ...styles.mixedWord,
                gridTemplateColumns: `repeat(6, ${cellSize}px)`,
                gap: isMobile ? "2px" : "3px",
              }}
            >
              {Array.from({ length: 6 }).map((_, i) => (
                (() => {
                  const letter = row.restLetters[i] || "";
                  const consumed = isLeftLetterConsumed(rowIndex, letter, i);
                  return (
                <button
                  key={`${row.id}-mixed-${i}`}
                  type="button"
                  onClick={() => handleLeftLetterClick(rowIndex, letter)}
                  style={{
                    ...styles.leftLetterButton,
                    width: `${cellSize}px`,
                    height: `${cellSize}px`,
                    fontSize: inputFontSize,
                    ...(consumed ? styles.leftLetterButtonDisabled : {}),
                  }}
                  disabled={consumed}
                >
                  {letter}
                </button>
                  );
                })()
              ))}
            </div>

            <div style={styles.arrow}>→</div>

            <div
              style={{
                ...styles.answerWord,
                gridTemplateColumns: `repeat(7, ${cellSize}px)`,
                gap: isMobile ? "2px" : "3px",
              }}
            >
              <div style={{ ...styles.answerCell, width: `${cellSize}px`, height: `${cellSize}px` }}>
                <div style={{ ...styles.square, ...styles.fixedSquare, width: `${cellSize}px`, height: `${cellSize}px`, fontSize: inputFontSize }}>
                  {row.firstLetter}
                </div>
                {row.phraseIndex === 1 && <div style={{ ...styles.innerPhraseCircle, inset: isMobile ? "1px" : "1px" }} />}
              </div>
              {Array.from({ length: 6 }).map((_, boxIndex) => (
                <div key={`${row.id}-box-${boxIndex}`} style={{ ...styles.answerCell, width: `${cellSize}px`, height: `${cellSize}px` }}>
                  <input
                    ref={(el) => {
                      if (!inputRefs.current[rowIndex]) inputRefs.current[rowIndex] = [];
                      inputRefs.current[rowIndex][boxIndex] = el;
                    }}
                    value={answers[rowIndex]?.[boxIndex] || ""}
                    onChange={(e) => handleLetterChange(rowIndex, boxIndex, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(rowIndex, boxIndex, e)}
                    maxLength={1}
                    style={{
                      ...styles.inputSquare,
                      width: `${cellSize}px`,
                      height: `${cellSize}px`,
                      fontSize: inputFontSize,
                      ...(giveUpRevealedCells[rowIndex]?.[boxIndex] ? styles.inputSquareRevealed : {}),
                    }}
                  />
                  {row.phraseIndex === boxIndex + 2 && <div style={styles.innerPhraseCircle} />}
                </div>
              ))}
            </div>

            <div style={styles.arrow}>→</div>

            <div
              style={{
                ...styles.circle,
                width: `${circleSize}px`,
                height: `${circleSize}px`,
                fontSize: inputFontSize,
                backgroundColor: phraseCircleColors[rowIndex] || styles.circleYellow.backgroundColor,
              }}
            >
              {phraseLetters[rowIndex]}
            </div>
          </div>
        ))}
      </div>
      <div style={styles.actions}>
        <button type="button" onClick={handleCheck} style={styles.checkButton}>
          Provjeri
        </button>
        <button type="button" onClick={() => setShowGiveUpModal(true)} style={styles.giveUpButton}>
          Odustajem
        </button>
      </div>

      {showGiveUpModal && (
        <div style={styles.modalOverlay} onClick={() => setShowGiveUpModal(false)}>
          <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <p style={styles.modalTitle}>Odustajem jer sam kognitivno ograničen</p>
            <div style={styles.modalActions}>
              <button type="button" style={styles.modalCancelButton} onClick={() => setShowGiveUpModal(false)}>
                Mozda ipak nisam
              </button>
              <button type="button" style={styles.modalConfirmButton} onClick={handleGiveUpConfirm}>
                Izgleda da da
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    fontFamily: "sans-serif",
    padding: "1.25rem 0.75rem 2rem",
    maxWidth: "1100px",
    margin: "0 auto",
  },
  title: {
    margin: 0,
    textAlign: "center",
  },
  titleHint: {
    margin: "0.15rem 0 0",
    textAlign: "center",
    fontSize: "0.85rem",
    color: "#666",
  },
  subtitle: {
    textAlign: "center",
    margin: "0.25rem 0 1rem",
    letterSpacing: "0.08em",
    color: "#555",
    fontSize: "1rem",
  },
  infoText: {
    textAlign: "center",
    margin: "0 0 0.75rem",
    color: "#555",
    fontSize: "0.95rem",
  },
  errorText: {
    textAlign: "center",
    margin: "0 0 0.75rem",
    color: "#b00020",
    fontSize: "0.95rem",
  },
  board: {
    display: "grid",
    gap: "0.4rem",
  },
  row: {
    display: "grid",
    gridTemplateColumns: "28px auto 24px auto 24px 36px",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.35rem",
  },
  rowStatus: {
    width: "28px",
    textAlign: "center",
    fontSize: "1.1rem",
  },
  mixedWord: {
    display: "grid",
    gridTemplateColumns: "repeat(6, 34px)",
    gap: "3px",
  },
  answerWord: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 34px)",
    gap: "3px",
  },
  square: {
    width: "34px",
    height: "34px",
    boxSizing: "border-box",
    border: "1px solid #777",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    textTransform: "uppercase",
    backgroundColor: "#fff",
  },
  leftLetterButton: {
    width: "34px",
    height: "34px",
    boxSizing: "border-box",
    border: "1px solid #777",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    textTransform: "uppercase",
    backgroundColor: "#fff",
    cursor: "pointer",
    padding: 0,
  },
  leftLetterButtonDisabled: {
    backgroundColor: "#e5e5e5",
    color: "#8a8a8a",
    cursor: "not-allowed",
  },
  fixedSquare: {
    backgroundColor: "#f4f4f4",
  },
  inputSquare: {
    width: "34px",
    height: "34px",
    border: "1px solid #777",
    boxSizing: "border-box",
    padding: 0,
    margin: 0,
    display: "block",
    textAlign: "center",
    fontSize: "1rem",
    fontWeight: 700,
    textTransform: "uppercase",
    outline: "none",
    backgroundColor: "#fff",
    position: "relative",
    zIndex: 1,
  },
  inputSquareRevealed: {
    backgroundColor: "#ffcdd2",
    borderColor: "#c62828",
    color: "#4a0000",
  },
  answerCell: {
    position: "relative",
    width: "34px",
    height: "34px",
  },
  innerPhraseCircle: {
    position: "absolute",
    inset: "1px",
    border: "1px solid #777",
    borderRadius: "50%",
    backgroundColor: "transparent",
    zIndex: 2,
    pointerEvents: "none",
  },
  arrow: {
    textAlign: "center",
    color: "#395e9a",
    fontWeight: 700,
  },
  circle: {
    width: "48px",
    height: "48px",
    border: "1px solid #777",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    backgroundColor: "#f8f2a7",
  },
  circleYellow: {
    backgroundColor: "#f8f2a7",
  },
  circleBlue: {
    backgroundColor: "#bfe8ff",
  },
  actions: {
    marginTop: "1rem",
    display: "flex",
    justifyContent: "center",
    gap: "0.6rem",
  },
  checkButton: {
    border: "none",
    borderRadius: "8px",
    padding: "0.65rem 1.1rem",
    fontSize: "1rem",
    cursor: "pointer",
    backgroundColor: "#2f7d32",
    color: "#fff",
    fontWeight: 700,
  },
  giveUpButton: {
    border: "none",
    borderRadius: "8px",
    padding: "0.65rem 1.1rem",
    fontSize: "1rem",
    cursor: "pointer",
    backgroundColor: "#757575",
    color: "#fff",
    fontWeight: 700,
  },
  modalOverlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0, 0, 0, 0.35)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: "1rem",
  },
  modalCard: {
    backgroundColor: "#fff",
    borderRadius: "10px",
    width: "100%",
    maxWidth: "600px",
    padding: "1rem",
    boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
  },
  modalTitle: {
    margin: 0,
    fontSize: "1.7rem",
    fontWeight: 700,
  },
  modalSubtitle: {
    margin: "0.6rem 0 0",
    color: "#444",
  },
  modalActions: {
    marginTop: "1rem",
    display: "flex",
    justifyContent: "flex-end",
    gap: "0.5rem",
  },
  modalCancelButton: {
    border: "1px solid #bbb",
    borderRadius: "8px",
    padding: "0.5rem 0.8rem",
    backgroundColor: "#fff",
    cursor: "pointer",
  },
  modalConfirmButton: {
    border: "none",
    borderRadius: "8px",
    padding: "0.5rem 0.8rem",
    backgroundColor: "#2f7d32",
    color: "#fff",
    fontWeight: 700,
    cursor: "pointer",
  },
};
